import { useState } from "react";
import { AlertTriangle, Navigation, Globe, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/utils/css";
import { TruckStatus, trailerTypeSymbols, truckBadgeStyles, truckStatusText, filterBadgeStyles } from "@/utils/constants";
import dayjs from "dayjs";

const LoadStatusBadge = ({ status }) => {
    const isFree = status === TruckStatus.FREE.id;
    const style = truckBadgeStyles[status] || truckBadgeStyles.FREE;
    const text = truckStatusText[status] || truckStatusText.FREE;

    return (
        <span
            className="flex items-center gap-1 text-[12px] font-medium uppercase tracking-tight pl-1.5 pr-2 py-1 rounded-md h-5"
            style={style}
        >
            {isFree && <AlertTriangle className="w-3 h-3" />}
            {text}
        </span>
    );
};

const FilterStatusBadge = ({ hasFilter }) => {
    const style = hasFilter ? filterBadgeStyles.HAS_FILTER : filterBadgeStyles.NO_FILTER;
    const text = hasFilter ? "Has filters" : "No filter";

    return (
        <span
            className="flex items-center gap-1 text-[12px] font-medium uppercase tracking-tight pl-1.5 pr-2 py-1 rounded-md h-5"
            style={style}
        >
            <Globe className="w-3 h-3" />
            {text}
        </span>
    );
};

const TrailerTypeBadge = ({ type, selected }) => {
    const symbol = trailerTypeSymbols[type] || "V";
    return (
        <span
            className={cn(
                "flex items-center justify-center w-4 h-5 text-[12px] font-semibold rounded text-cm-p-text-primary",
                selected ? "bg-cm-p-accent-100" : "bg-cm-p-surface-container",
            )}
        >
            {symbol}
        </span>
    );
};

export const TruckCard = ({
    truck,
    selected,
    onClick,
    hasFilter = false,
    hasNewLoads = false,
    onEdit,
    onDelete,
    readOnly = false,
    hoverHighlight = true,
}) => {
    const [isHovered, setIsHovered] = useState(false);

    const truckName = truck.truckName;
    const driverName = truck.driverName;
    const trailerType = truck.trailerType;
    const status = truck.status || TruckStatus.FREE.id;
    const location = truck.location ? `${truck.location.city}, ${truck.location.state}` : null;
    const deliveryDate = truck.deliveryDate ? dayjs(truck.deliveryDate).format("M/D") : null;
    const isLoaded = status === TruckStatus.LOADED.id;
    const isOffDuty = status === TruckStatus.OFF_DUTY.id;
    const dateText = deliveryDate
        ? isLoaded
            ? ` on ${deliveryDate}`
            : isOffDuty
              ? `, ready ${deliveryDate}`
              : `, available from ${deliveryDate}`
        : "";
    const showHoverActions = isHovered && !readOnly;

    return (
        <div
            className={cn(
                "rounded-lg border px-3 py-2 transition-colors",
                !readOnly && "cursor-pointer",
                selected
                    ? "bg-cm-p-container-green border-cm-p-accent-100"
                    : isHovered && !readOnly && hoverHighlight
                      ? "bg-cm-p-surface-hover border-cm-p-outline"
                      : "bg-cm-p-surface border-cm-p-outline",
            )}
            onClick={readOnly ? undefined : onClick}
            onMouseEnter={readOnly ? undefined : () => setIsHovered(true)}
            onMouseLeave={readOnly ? undefined : () => setIsHovered(false)}
        >
            {/* Upper part */}
            <div className="flex flex-col gap-0">
                {/* Truck name row */}
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        {hasNewLoads && <span className="w-2 h-2 rounded-full bg-cm-p-accent-500 shrink-0" />}
                        <span className="text-[16px] font-medium text-cm-p-text-primary truncate">
                            {driverName || truckName}
                        </span>
                    </div>
                    {showHoverActions ? (
                        <div className="flex items-center gap-1 shrink-0">
                            <button
                                className="w-6 h-6 flex items-center justify-center text-cm-p-text-secondary hover:text-cm-p-text-primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit?.(truck);
                                }}
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button
                                className="w-6 h-6 flex items-center justify-center text-cm-p-text-secondary hover:text-cm-p-on-container-red"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete?.(truck);
                                }}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        // Truck number sits on the right; it's redundant when it's already the title
                        // (no driver name, or a migrated truck whose number equals the driver name),
                        // in which case we leave the right side empty.
                        driverName && driverName !== truckName && (
                            <span className="text-[14px] text-cm-p-text-secondary shrink-0">{truckName}</span>
                        )
                    )}
                </div>

                {/* Pills row */}
                <div className="flex items-center gap-1 py-0.5">
                    <TrailerTypeBadge type={trailerType} selected={selected} />
                    <LoadStatusBadge status={status} />
                    <FilterStatusBadge hasFilter={hasFilter} />
                </div>
            </div>

            {/* Info section */}
            <div className="flex flex-col mt-2">
                {/* Location info — off-duty trucks have no location, so show "No location" to keep
                    the card the same height as cards that do show one. */}
                <div className="flex items-center gap-2">
                    <Navigation className="w-4 h-4 text-cm-p-text-secondary shrink-0" />
                    <span className="text-[14px] text-cm-p-text-secondary">
                        {location ? (
                            <>
                                {isLoaded ? "Delivering to " : "In "}
                                {location}
                                {dateText}
                            </>
                        ) : (
                            `No location${dateText}`
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
};
